import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * Input Toggle
 *--------------------------------------------------------------------------
 *
 * Class to toggle secondary form fields
 *
 */

export default class InputToggle {

	/**
	 * The form element being validated.
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Constructor of `InputToggle`
	 * @param  {HTMLInputElement} input
	 */
	constructor (input) {
		this.#validator = new ParameterValidator(this);

		// Get the container element to toggle
		const containerId = input.getAttribute('aria-controls');
		const containerElement = document.getElementById(containerId);

		// Set toggle fields
		/**
		 * A variable reference to the primary input field that will trigger the toggled secondary form fields.
		 * @prop {HTMLInputElement}
		 */
		this.toggleField = this.#validator.isElement(`toggleField`, input);

		/**
		 * The value to check against to toggle the secondary form fields.
		 * @prop {String}
		 */
		this.toggleCondition = this.getToggleCondition();

		/**
		 * If true will reset the secondary fields' value if the toggle condition is not met.
		 * @prop {Boolean}
		 */
		this.toggleResetCondition = this.getToggleResetCondition();

		/**
		 * A reference to the container that will be used to toggle the secondary form fields.
		 * @prop {HTMLElement}
		 */
		this.containerToToggle = this.#validator.isElement(`containerToToggle`, containerElement);

		/**
		 * A reference to all of the secondary form fields to be toggled.
		 * @prop {Object}
		 */
		this.containerToToggleFields = this.getToggleContainerFormFields();

		// Init
		this.init();
	}

	/**
	 * Entry point function
	 * Bind events and check the initial state of the primary input element
	 */
	init () {
		this.bindEvents();
		this.checkIfToggleConditionIsMet();
	}

	/**
	 * Binds events to the primary input element
	 */
	bindEvents () {
		this.toggleField.addEventListener('input', this.onInput.bind(this));
	}

	/**
	 * Check and determine the toggle state on input change
	 */
	onInput () {
		this.checkIfToggleConditionIsMet();
	}

	/**
	 * Get the toggle condition
	 * @return {String} the condition to check against.
	 */
	getToggleCondition () {
		const condition = this.toggleField.dataset.inputToggleCondition && this.toggleField.dataset.inputToggleCondition.length > 0 ? this.toggleField.dataset.inputToggleCondition : null;

		return condition;
	}

	/**
	 * Get the toggle reset condition
	 * @return {Boolean} this will be used to determine whether or not form fields should be reset.
	 */
	getToggleResetCondition () {
		const reset = this.toggleField.dataset.inputToggleReset && this.toggleField.dataset.inputToggleReset === 'true';

		return reset;
	}

	/**
	 * Get the toggle container's form fields
	 * @return {Object} the toggled container's form fields
	 */
	getToggleContainerFormFields () {
		const inputs = this.containerToToggle.getElementsByTagName('input');
		const selects = this.containerToToggle.getElementsByTagName('select');
		const memos = this.containerToToggle.getElementsByTagName('textarea');

		const fields = {
			inputs: inputs,
			memos: memos,
			selects: selects
		};

		return fields;
	}

	/**
	 * Determine visibility by checking the primary input's value since no condition was provided
	 */
	toggleVisibilityByValueLength () {
		const checked = this.toggleField.checked;
		const tag = this.toggleField.tagName;
		const type = this.toggleField.type;
		const value = this.toggleField.value;

		// eslint-disable-next-line no-extra-parens
		if ((tag === 'INPUT' && type === 'checkbox' && !checked) || value.length === 0) {
			this.hideToggleContainerElement();
		} else {
			this.showToggleContainerElement();
		}
	}

	/**
	 * Determine visibility by checking the primary input's value against the set condition
	 */
	toggleVisibilityByMatch () {
		const value = this.toggleField.value;
		const condition = this.toggleCondition;

		if (value === condition) {
			this.showToggleContainerElement();
		} else {
			this.hideToggleContainerElement();
		}
	}

	/**
	 * Reset all container input form fields
	 * @param {HTMLCollection} inputs - The inputs to reset, currently set to only reset certain types of inputs.
	 */
	resetContainerInputFields (inputs) {
		if (!inputs) {
			return;
		}

		for (const input of inputs) {
			let type = input.type;

			if (type === 'radio' || type === 'checkbox') {
				input.checked = false;
			} else {
				input.value = input.defaultValue;
			}
		}
	}

	/**
	 * Reset all container select form fields
	 * @param {HTMLCollection} selects - The select form fields to reset
	 */
	resetContainerSelectFields (selects) {
		if (!selects) {
			return;
		}

		for (const select of selects) {
			select.selectedIndex = 0;
		}
	}

	/**
	 * Reset all container textarea form fields
	 * @param {HTMLCollection} memos - The textarea form fields to reset
	 */
	resetContainerTextAreaFields (memos) {
		if (!memos) {
			return;
		}

		for (const memo of memos) {
			memo.value = '';
		}
	}

	/**
	 * Reset all container form fields
	 */
	resetContainerFormFields () {
		this.resetContainerInputFields(this.containerToToggleFields.inputs);
		this.resetContainerSelectFields(this.containerToToggleFields.selects);
		this.resetContainerTextAreaFields(this.containerToToggleFields.memos);
	}

	/**
	 * Hide the container element
	 */
	hideToggleContainerElement () {
		if (this.toggleResetCondition) {
			this.resetContainerFormFields();
		}

		this.containerToToggle.classList.add('u-hidden');
		this.containerToToggle.setAttribute('aria-hidden', true);
	}

	/**
	 * Show the container element
	 */
	showToggleContainerElement () {
		this.containerToToggle.classList.remove('u-hidden');
		this.containerToToggle.setAttribute('aria-hidden', false);
	}

	/**
	 * Check if we need to validate against any conditions to toggle the additional container display
	 */
	checkIfToggleConditionIsMet () {
		if (this.toggleCondition) {
			this.toggleVisibilityByMatch();
		} else {
			this.toggleVisibilityByValueLength();
		}
	}
}
